/**
 * The Deal Score callout on the product page: who qualifies, which arm a
 * browser is in, and how a score reads to a buyer. Pure and browser-safe; the
 * product page uses it to decide whether to ask, the server to decide the
 * answer (pages/api/deal-callout.ts).
 *
 * Owner, 2026-10-10: show it "only when the score is above 0 and confidence is
 * medium or high", in three arms -- no callout, a "% below market + $ saved"
 * badge, an illustrated gauge -- to test whether a callout moves conversion.
 *
 * On prod that day 20,800 live organic listings qualified, about one in ten:
 * 12,600 against the market-estimate pass, 6,800 against the tagger's text
 * estimate, 1,200 against Jev-checked comparables, and a few hundred from the
 * photo and seller passes.
 */

import { dealScoreDetailFromParsed } from './deal-score';
import { hashKey } from './ranking-experiments';
import {
  DEAL_CALLOUT_ARMS,
  type DealCallout,
  type DealCalloutArm,
} from '../schemas/api/deal-callout';

/**
 * Estimate sources the buyer never sees a callout for, whatever their
 * confidence. `neighbors` is medium for ranking (owner, 2026-10-10), but it is
 * the median of the nearest listings with no judge, and on prod it priced a
 * $300 body cream against $506 and a $27 dress against $124 -- the neighbours
 * were bags and designer dresses. It stays off the callout until Jev's
 * comparables replace it (owner: "yes").
 */
export const CALLOUT_EXCLUDED_SOURCES: ReadonlySet<unknown> = new Set(['neighbors']);

/**
 * The lowest score a buyer sees a callout for: "Great price" and up (owner,
 * 2026-10-10). Of 20,855 listings that qualified on prod that day, 19,870 read
 * "Good price", often for a few dollars off; at 25 about 985 remain, each a
 * real discount.
 */
export const MIN_CALLOUT_SCORE = 25;

/** SYSTEM_FLAGS row. Off, or no row, means only admins see a callout. */
export const FLAG_DEAL_CALLOUT_AB = 'deal_callout_ab';

/** Where the exposure is recorded in ClickHouse funnel_events. */
export const DEAL_CALLOUT_FUNNEL = 'deal_callout';
export const DEAL_CALLOUT_EXPOSED = 'exposed';

/** `?deal_callout=badge` on a product URL previews an arm. Never counted. */
export const DEAL_CALLOUT_PREVIEW_PARAM = 'deal_callout';

/**
 * What a listing's callout would say, or null when it does not qualify:
 * no estimate, a source that never gets a deal score, a score under
 * MIN_CALLOUT_SCORE, a low
 * confidence estimate, or an estimate from the unjudged nearest listings.
 */
export function dealCalloutFor(
  analysis: unknown,
  price: number | string | null | undefined,
  dataSourceId: number | null | undefined,
): DealCallout | null {
  if (!analysis || typeof analysis !== 'object') return null;
  if (CALLOUT_EXCLUDED_SOURCES.has((analysis as { estimateSource?: unknown }).estimateSource)) return null;
  const detail = dealScoreDetailFromParsed(analysis, price, dataSourceId);
  if (!detail || detail.score < MIN_CALLOUT_SCORE) return null;
  if (detail.confidence !== 'medium' && detail.confidence !== 'high') return null;

  // A score at MIN_CALLOUT_SCORE or above only comes from dealScoreBreakdown's
  // scored branch, which has a reference, positive savings, and (with the score
  // capped by discount / DEEP_DISCOUNT) a discount of at least 17%.
  const percentBelow = Math.round(detail.discount * 100);

  return {
    score: Math.min(100, detail.score),
    percentBelow: Math.min(100, percentBelow),
    savings: detail.savings,
    reference: detail.reference,
    confidence: detail.confidence,
  };
}

/**
 * A browser's arm. Salted, so it is independent of the ranking experiment's
 * buckets, which hash the same anonymous id: unsalted, every ranking arm would
 * have lined up with one callout arm and the two tests would measure each other.
 */
export function armForKey(key: string): DealCalloutArm {
  return DEAL_CALLOUT_ARMS[hashKey(`deal-callout:${key.trim()}`) % DEAL_CALLOUT_ARMS.length];
}

/** A preview arm from the URL, or undefined when there is none or it is not an arm. */
export function previewArm(value: unknown): DealCalloutArm | undefined {
  const raw = Array.isArray(value) ? value[0] : value;
  return (DEAL_CALLOUT_ARMS as readonly unknown[]).includes(raw) ? (raw as DealCalloutArm) : undefined;
}

/**
 * How the score reads. The bands follow the score's own scale: 100 is 70%
 * off with $100 or more saved, so 60 is already a large, well-founded discount.
 */
export function dealLabel(score: number): string {
  if (score >= 60) return 'Exceptional price';
  if (score >= 25) return 'Great price';
  return 'Good price';
}

/** Dollars as a buyer reads them: whole dollars from $10, cents below. */
export function formatDealDollars(value: number): string {
  if (value >= 10) return `$${Math.round(value).toLocaleString('en-US')}`;
  return `$${value.toFixed(2)}`;
}
